<template>
    <div class="bg-white dark:bg-dark-900 overflow-hidden rounded-lg shadow-lg pb-24 z-0">

        <!-- Orders Grid -->
        <div class="p-2">
            <div class="grid md:grid-cols-2 grid-cols-1 gap-2">
                <div v-if="filteredOrders.length == 0" class="col-span-full text-center italic text-gray-400 p-4">
                    Belum ada data
                </div>
                <div v-else v-for="(order) in filteredOrders" :key="order.id" class="relative group">
                    <button :id="`edit-order-${order.id}`" type="button" class="w-full text-left"
                        @click="$emit('edit-order', order)">
                        <div class="flex flex-col transition-all duration-200 shadow-md hover:shadow-lg rounded-lg relative hover:scale-105"
                            :class="[getOrderStyles(order.status), isTakeAwayOrder(order) ? 'p-3' : 'p-4']">
                            <!-- Order Number and Status -->
                            <div class="absolute top-1 left-2 right-2 flex justify-between items-center">
                                <span class="text-xs font-light tracking-tight text-gray-500 dark:text-gray-400">
                                    {{ isTakeAwayOrder(order) ? formatOrderType(order.order_type) : order.code }}
                                </span>
                                <span class="text-xs text-gray-600 dark:text-gray-300">
                                    {{ formatTime(order.createdAt) }}
                                </span>
                            </div>

                            <!-- Center Content -->
                            <div class="flex-1 flex flex-col items-center justify-center">
                                <!-- Customer Name -->
                                <h3 class="font-bold mb-1" :class="[getOrderTextStyles(order.status), isTakeAwayOrder(order) ? 'text-2xl md:text-3xl' : 'text-2xl']">
                                    {{ getOrderTitle(order) }}
                                </h3>

                                <!-- Status Badge -->
                                <span class="text-xs font-medium px-3 py-0.5 rounded-full mb-1"
                                    :class="getStatusBadgeStyles(order.status)">
                                    {{ formatStatus(order.status) }}
                                </span>

                                <!-- Order Details -->
                                <div class="flex flex-col items-center" :class="isTakeAwayOrder(order) ? 'gap-1' : 'gap-2'">
                                    <div v-if="getRelevantTable(order)" class="flex items-center gap-1.5 dark:text-dark-500">
                                        <span class="flex items-center gap-1 text-xs" :class="getOrderTextStyles(order.status)">
                                            <UserGroupIcon class="h-4 w-4" />
                                            {{ getRelevantTable(order)?.capacity }}
                                        </span>
                                    </div>
                                    <span class="text-xs" :class="getOrderTextStyles(order.status)">
                                        {{ order.customer_name || 'Tanpa Nama' }}
                                    </span>
                                    <!-- <div class="flex items-center gap-2">
                                        <QueueListIcon class="h-4 w-4" :class="getOrderTextStyles(order.status)" />
                                        <span class="text-sm" :class="getOrderTextStyles(order.status)">
                                            {{ formatOrderType(order.order_type) || 'Tidak ada tipe' }}
                                        </span>
                                    </div> -->
                                </div>
                            </div>
                        </div>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { UserGroupIcon } from '@heroicons/vue/24/outline'

export default {
    name: 'CardOrder',
    components: {
        UserGroupIcon,
    },
    props: {
        orders: {
            type: Array,
            required: true
        },
        tables: {
            type: Array,
            required: true
        }
    },
    emits: ['edit-order'],
    data() {
        return {
            searchQuery: '',
            statusFilter: '',
            orderStatuses: ['pending', 'processing', 'completed', 'cancelled']
        }
    },
    computed: {
        filteredOrders() {
            return this.orders.filter(order => {
                const customerName = order.customer_name || '';
                const matchesSearch = customerName.toLowerCase().includes(this.searchQuery.toLowerCase());
                const matchesStatus = !this.statusFilter || order.status === this.statusFilter;
                return matchesSearch && matchesStatus;
            });
        }
    },
    methods: {
        formatStatus(status) {
            const statusMap = {
                'pending': 'Waiting',
                'processing': 'Diproses',
                'completed': 'Selesai',
                'cancelled': 'Dibatalkan'
            };
            return statusMap[status] || status;
        },
        formatTime(timestamp) {
            const date = new Date(timestamp);
            return date.toLocaleTimeString('id-ID', {
                hour: '2-digit',
                minute: '2-digit'
            });
        },
        formatOrderType(type) {
            const typeMap = {
                'dine_in': 'Dine in',
                'take_away': 'Take away',
                'delivery': 'Delivery',
                'staff': 'Staff'
            };
            return typeMap[type] || type;
        },
        getOrderStyles(status) {
            const styles = {
                'pending': 'bg-yellow-50 dark:bg-yellow-900/30 border-2 border-yellow-500',
                'processing': 'bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-500',
                'completed': 'bg-emerald-50 dark:bg-emerald-900/30 border-2 border-emerald-500',
                'cancelled': 'bg-red-50 dark:bg-red-900/30 border-2 border-red-500'
            };
            return styles[status] || 'bg-gray-50 dark:bg-gray-900/30 border-2 border-gray-500';
        },
        getOrderTextStyles(status) {
            const styles = {
                'pending': 'text-yellow-700 dark:text-yellow-300',
                'processing': 'text-blue-700 dark:text-blue-300',
                'completed': 'text-emerald-700 dark:text-emerald-300',
                'cancelled': 'text-red-700 dark:text-red-300'
            };
            return styles[status] || 'text-gray-700 dark:text-gray-300';
        },
        getStatusBadgeStyles(status) {
            const styles = {
                'pending': 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300',
                'processing': 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300',
                'completed': 'bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300',
                'cancelled': 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300'
            };
            return styles[status] || 'bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300';
        },
        getRelevantTable(order) {
            if (!order.table_id || !this.tables) return null;
            return this.tables.find(table => table.id === order.table_id);
        },
        getOrderTitle(order) {
            if (this.isTakeAwayOrder(order)) {
                return order.code || this.formatOrderType(order.order_type);
            }
            return this.getRelevantTable(order)?.name || this.formatOrderType(order.order_type);
        },
        isTakeAwayOrder(order) {
            return !order?.table_id && order?.order_type === 'take_away';
        }
    }
}
</script>
